import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { Week, Day, TrainingBlock, BlockItem, BlockType } from '../types';

const blockTypeLabels: Record<BlockType, string> = {
  warmup: 'Entrada en Calor',
  exercise: 'Ejercicios',
  circuit: 'Circuito',
  superset: 'Super Serie',
  amrap: 'AMRAP',
  emom: 'EMOM',
  for_time: 'For Time',
  interval: 'Intervalos',
};

const dayNames = ['', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
const shiftLabels: Record<string, string> = {
  morning: 'Mañana',
  afternoon: 'Tarde',
  evening: 'Noche',
};
const disciplineLabels: Record<string, string> = {
  bodybuilding: 'Musculación',
  powerlifting: 'Fuerza / Powerlifting',
  weightlifting: 'Halterofilia',
  crossfit: 'CrossFit / Funcional',
  cardio: 'Cardio / Resistencia',
  calisthenics: 'Calistenia',
  hybrid: 'Híbrido',
  general: 'Acondicionamiento general',
};

interface PlanWithDetails {
  id: string;
  name: string;
  description?: string;
  discipline?: string | null;
  startDate: string;
  endDate?: string;
  isActive: boolean;
  studentId: string;
  student?: {
    firstName: string;
    lastName: string;
  };
  weeks?: Week[];
}

export function exportPlanToPDF(plan: PlanWithDetails): void {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  let yPosition = 20;

  // Colors
  const primaryColor: [number, number, number] = [234, 179, 8]; // yellow-500 (amarillo fuerte)
  const darkColor: [number, number, number] = [15, 23, 42]; // dark-900
  const grayColor: [number, number, number] = [100, 116, 139]; // dark-500

  // Helper function to add new page if needed
  const checkPageBreak = (height: number) => {
    if (yPosition + height > doc.internal.pageSize.getHeight() - 20) {
      doc.addPage();
      yPosition = 20;
    }
  };

  // ========== HEADER ==========
  doc.setFillColor(...primaryColor);
  doc.rect(0, 0, pageWidth, 40, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text(plan.name, 15, 25);

  if (plan.student) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Alumno: ${plan.student.firstName} ${plan.student.lastName}`, 15, 35);
  }

  yPosition = 55;

  // ========== PLAN INFO ==========
  doc.setTextColor(...darkColor);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');

  const startDate = new Date(plan.startDate).toLocaleDateString('es-AR');
  const endDate = plan.endDate ? new Date(plan.endDate).toLocaleDateString('es-AR') : 'Sin definir';
  const status = plan.isActive ? 'Activo' : 'Finalizado';

  doc.setTextColor(...grayColor);
  const disciplineLabel = plan.discipline ? disciplineLabels[plan.discipline] ?? plan.discipline : null;
  const infoLine = `Fecha inicio: ${startDate}  |  Fecha fin: ${endDate}  |  Estado: ${status}${
    disciplineLabel ? `  |  Disciplina: ${disciplineLabel}` : ''
  }`;
  doc.text(infoLine, 15, yPosition);
  yPosition += 8;

  if (plan.description) {
    doc.setTextColor(...darkColor);
    doc.setFontSize(10);
    const descLines = doc.splitTextToSize(plan.description, pageWidth - 30);
    doc.text(descLines, 15, yPosition);
    yPosition += descLines.length * 5 + 5;
  }

  yPosition += 10;

  // ========== WEEKS ==========
  if (plan.weeks && plan.weeks.length > 0) {
    plan.weeks.forEach((week: Week) => {
      checkPageBreak(30);

      // Week header
      doc.setFillColor(241, 245, 249); // slate-100
      doc.rect(10, yPosition - 5, pageWidth - 20, 12, 'F');
      
      doc.setTextColor(...primaryColor);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(`Semana ${week.weekNumber}${week.name ? ` - ${week.name}` : ''}`, 15, yPosition + 3);
      yPosition += 15;

      if (week.notes) {
        doc.setTextColor(...grayColor);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'italic');
        const notesLines = doc.splitTextToSize(week.notes, pageWidth - 30);
        doc.text(notesLines, 15, yPosition);
        yPosition += notesLines.length * 4 + 5;
      }

      // Days
      if (week.days && week.days.length > 0) {
        week.days.forEach((day: Day) => {
          checkPageBreak(20);

          // Day header
          doc.setTextColor(...darkColor);
          doc.setFontSize(11);
          doc.setFont('helvetica', 'bold');
          
          const shiftSuffix = day.shift ? ` (${shiftLabels[day.shift] ?? day.shift})` : '';
          const dayTitle = `${dayNames[day.dayNumber]}${shiftSuffix}${day.name ? ` - ${day.name}` : ''}`;
          doc.text(dayTitle, 15, yPosition);
          
          if (day.restDay) {
            doc.setTextColor(...grayColor);
            doc.setFontSize(9);
            doc.setFont('helvetica', 'italic');
            doc.text(' (Día de descanso)', 15 + doc.getTextWidth(dayTitle) + 2, yPosition);
          }
          yPosition += 8;

          // Blocks and items
          if (day.blocks && day.blocks.length > 0 && !day.restDay) {
            day.blocks.forEach((block: TrainingBlock) => {
              checkPageBreak(15);

              // Block header
              doc.setTextColor(...primaryColor);
              doc.setFontSize(10);
              doc.setFont('helvetica', 'bold');
              const blockTitle = block.name || blockTypeLabels[block.type as BlockType] || block.type;
              doc.text(`▸ ${blockTitle}`, 20, yPosition);
              yPosition += 6;

              if (block.notes) {
                doc.setTextColor(...grayColor);
                doc.setFontSize(8);
                doc.setFont('helvetica', 'italic');
                doc.text(block.notes, 25, yPosition);
                yPosition += 5;
              }

              // Items table
              if (block.items && block.items.length > 0) {
                const tableData = block.items.map((item: BlockItem) => [
                  item.exercise?.name || 'Ejercicio',
                  item.sets ? `${item.sets}` : '-',
                  item.reps || '-',
                  item.weight || '-',
                  item.restSeconds ? `${item.restSeconds}s` : '-',
                  item.notes || '',
                ]);

                autoTable(doc, {
                  startY: yPosition,
                  head: [['Ejercicio', 'Series', 'Reps', 'Peso', 'Descanso', 'Notas']],
                  body: tableData,
                  margin: { left: 25, right: 15 },
                  styles: {
                    fontSize: 8,
                    cellPadding: 2,
                  },
                  headStyles: {
                    fillColor: primaryColor,
                    textColor: [255, 255, 255],
                    fontStyle: 'bold',
                  },
                  alternateRowStyles: {
                    fillColor: [248, 250, 252], // slate-50
                  },
                  columnStyles: {
                    0: { cellWidth: 50 },
                    1: { cellWidth: 15, halign: 'center' },
                    2: { cellWidth: 20, halign: 'center' },
                    3: { cellWidth: 25, halign: 'center' },
                    4: { cellWidth: 20, halign: 'center' },
                    5: { cellWidth: 'auto' },
                  },
                });

                yPosition = (doc as any).lastAutoTable.finalY + 8;
              } else {
                yPosition += 5;
              }
            });
          }

          yPosition += 5;
        });
      }

      yPosition += 10;
    });
  } else {
    doc.setTextColor(...grayColor);
    doc.setFontSize(12);
    doc.text('Este plan no tiene semanas programadas.', 15, yPosition);
  }

  // ========== FOOTER ==========
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(...grayColor);
    doc.text(
      `Generado el ${new Date().toLocaleDateString('es-AR')} - Página ${i} de ${pageCount}`,
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: 'center' }
    );
  }

  // Save the PDF
  const fileName = `${plan.name.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
}
